import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  HostListener
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GlobalFilterService } from '../../shared-services/global-filter-service';

export interface GlobalFilterBase {
  name?: string;
  key?: string;                       // e.g. 'dates'
  type?: 'date-calender' | string;
  isMultiSelect?: boolean;            // default true
  isQueryParam?: boolean;             // <-- add this line
  // Optional display/limits
  minDate?: Date | string;
  maxDate?: Date | string;
  firstDayOfWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6; // default 1 (Mon)
}


type DayCell = {
  date: Date;
  inCurrentMonth: boolean;
  disabled: boolean;
  selected: boolean;
};

@Component({
  selector: 'date-calender',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateCalenderComponent),
      multi: true,
    },
  ],
  templateUrl: './date-calender.component.html',
  styleUrl: './date-calender.component.scss', // safe even if file is empty; create later if needed
})
export class DateCalenderComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() data: GlobalFilterBase | null = null;
  @Output() filterChanged = new EventEmitter<string[]>(); // emits ['YYYY-MM-DD', ...]

  // internal selection as Date[]
  value: Date[] = [];

  // calendar view
  viewYear = new Date().getFullYear();
  viewMonth = new Date().getMonth(); // 0..11
  weeks: DayCell[][] = [];

  private onChangeFn: (v: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  constructor(private globalFilterService: GlobalFilterService) {}

showYearPicker = false;
yearStart = 0;
yearGrid: number[] = [];
monthShort = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

toggleYearPicker(evt: MouseEvent) {
  evt.stopPropagation();
  this.showYearPicker = !this.showYearPicker;
  if (this.showYearPicker) {
    // start at the beginning of a 12-year block centered on current viewYear
    this.yearStart = this.viewYear - (this.viewYear % 12);
    this.buildYearGrid();
  }
}

shiftYears(delta: number) {
  this.yearStart += delta;
  this.buildYearGrid();
}

private buildYearGrid() {
  this.yearGrid = Array.from({ length: 12 }, (_, i) => this.yearStart + i);
}

selectYear(y: number) {
  this.viewYear = y;
  this.generateCalendar();
  // keep the picker open so user can also pick month, or close here if you prefer
}

selectMonth(i: number) {
  this.viewMonth = i;
  this.generateCalendar();
  this.showYearPicker = false; // close and show the month grid
}


  isOpen = false;

togglePopover(evt?: MouseEvent) {
  evt?.stopPropagation();
  this.isOpen = !this.isOpen;
}

// Close when clicking anywhere outside
@HostListener('document:click')
closeOnOutsideClick() {
  if (this.isOpen) this.isOpen = false;
}

// Optional: quick jump to today’s month
goToToday() {
  const t = new Date();
  this.viewYear = t.getFullYear();
  this.viewMonth = t.getMonth();
  this.generateCalendar();
  this.syncSelections();
}


  // Lifecycle
  ngOnInit(): void {
    this.generateCalendar();
    this.syncSelections();
  }

  ngOnChanges(_: SimpleChanges): void {
    this.generateCalendar();
    this.syncSelections();
  }

  // CVA
  writeValue(val: any): void {
    if (!val) {
      this.value = [];
    } else if (Array.isArray(val)) {
      this.value = val.map((v) => this.toDate(v)).filter(Boolean) as Date[];
    } else {
      this.value = [];
    }
    this.syncSelections();
  }
  registerOnChange(fn: any): void { this.onChangeFn = fn; }
  registerOnTouched(fn: any): void { this.onTouchedFn = fn; }
  setDisabledState?(isDisabled: boolean): void {}

  // Header
  get monthLabel(): string {
    return new Date(this.viewYear, this.viewMonth, 1).toLocaleString(undefined, {
      month: 'long', year: 'numeric',
    });
  }
  get weekDayLabels(): string[] {
    const first = this.data?.firstDayOfWeek ?? 1;
    const base = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return base.slice(first).concat(base.slice(0, first));
  }

  prevMonth(): void {
    if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear--; }
    else { this.viewMonth--; }
    this.generateCalendar();
    this.syncSelections();
  }
  nextMonth(): void {
    if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear++; }
    else { this.viewMonth++; }
    this.generateCalendar();
    this.syncSelections();
  }

  // Grid
  private generateCalendar(): void {
    const firstDayOfWeek = this.data?.firstDayOfWeek ?? 1;
    const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
    const startDay = (firstOfMonth.getDay() - firstDayOfWeek + 7) % 7;

    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    const prevMonth = this.viewMonth === 0 ? 11 : this.viewMonth - 1;
    const prevYear = this.viewMonth === 0 ? this.viewYear - 1 : this.viewYear;
    const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

    const min = this.data?.minDate ? this.stripTime(this.toDate(this.data.minDate)!) : null;
    const max = this.data?.maxDate ? this.stripTime(this.toDate(this.data.maxDate)!) : null;

    const cells: DayCell[] = [];

    // leading
    for (let i = 0; i < startDay; i++) {
      const day = daysInPrevMonth - startDay + 1 + i;
      const d = new Date(prevYear, prevMonth, day);
      cells.push(this.makeCell(d, false, min, max));
    }
    // current
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(this.viewYear, this.viewMonth, day);
      cells.push(this.makeCell(d, true, min, max));
    }
    // trailing to 42
    const total = 42;
    const rest = total - cells.length;
    const nextMonth = (this.viewMonth + 1) % 12;
    const nextYear = this.viewMonth === 11 ? this.viewYear + 1 : this.viewYear;
    for (let i = 1; i <= rest; i++) {
      const d = new Date(nextYear, nextMonth, i);
      cells.push(this.makeCell(d, false, min, max));
    }

    this.weeks = [];
    for (let i = 0; i < total; i += 7) {
      this.weeks.push(cells.slice(i, i + 7));
    }
  }

  private makeCell(d: Date, inCurrentMonth: boolean, min: Date | null, max: Date | null): DayCell {
    const disabled = this.isOutOfRange(d, min, max);
    return {
      date: d,
      inCurrentMonth,
      disabled,
      selected: this.isSelected(d),
    };
  }

  private isOutOfRange(d: Date, min: Date | null, max: Date | null): boolean {
    const s = +this.stripTime(d);
    if (min && s < +min) return true;
    if (max && s > +max) return true;
    return false;
  }
  private stripTime(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  // Selection
  onDayClick(cell: DayCell): void {
    if (cell.disabled) return;

    const multi = this.data?.isMultiSelect !== false; // default true
    const key = +this.stripTime(cell.date);

    if (multi) {
      const idx = this.value.findIndex((d) => +this.stripTime(d) === key);
      if (idx >= 0) this.value.splice(idx, 1);
      else this.value.push(new Date(cell.date));
    } else {
      const isSame = this.value.length === 1 && +this.stripTime(this.value[0]) === key;
      this.value = isSame ? [] : [new Date(cell.date)];
    }

    this.syncSelections();
    this.propagate(); // update GF + emit
  }

  clearAll(): void {
    this.value = [];
    this.syncSelections();
    this.propagate();
  }

  private isSelected(d: Date): boolean {
    const day = +this.stripTime(d);
    return this.value.some((v) => +this.stripTime(v) === day);
  }

  private syncSelections(): void {
    // refresh flags
    this.weeks.forEach((w) => w.forEach((c) => (c.selected = this.isSelected(c.date))));
  }

  // Emission
 private propagate(): void {
  this.onChangeFn(this.value.slice());
  this.onTouchedFn();

  const out = this.value.map(d => this.ymd(d)).sort();

  if (this.data?.key) {
    this.globalFilterService.activeFilter = this.data.key;
    this.globalFilterService.updateFilterSelection(
      this.data.key,
      out,
      this.data?.isQueryParam ?? false
    );
  }

  this.filterChanged.emit(out);
}


  // Utils
  private toDate(v: Date | string): Date | null {
    if (!v) return null;
    if (v instanceof Date) return v;
    const d = new Date(v);
    return isNaN(+d) ? null : d;
  }
  private ymd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
